import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePackages } from '../context/PackageContext';
import { getImagePath, siteConfig } from '../config/siteConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookingForm from '../components/BookingForm';
import '../styles/TripDetails.css';

const TripDetails = () => {
  const { company } = siteConfig;
  const { tripId } = useParams();
  const { packages } = usePackages();
  const [trip, setTrip] = useState(null);

  const handleDocumentDownload = (doc) => {
    const pdfPath = getImagePath(`/assets/pdfs/${doc.name}`);
    
    // Use fetch to download the file as a blob to handle special characters
    fetch(pdfPath)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Download error:', error);
        alert('Failed to download the file. Please try again.');
      });
  };

  useEffect(() => {
    // Find the trip by matching the link
    const foundTrip = packages.find(pkg => pkg.link === `/trip-details/${tripId}`);
    setTrip(foundTrip);
    window.scrollTo(0, 0);

    // Parallax effect for hero image
    const handleParallax = () => {
      const heroSection = document.querySelector('.trip-hero-modern');
      if (heroSection) {
        const scrolled = window.pageYOffset;
        const heroHeight = heroSection.offsetHeight;
        if (scrolled <= heroHeight) {
          const heroImg = document.querySelector('.hero-bg-img');
          if (heroImg) {
            heroImg.style.transform = `translateY(${scrolled * 0.5}px) scale(1.1)`;
          }
        }
      }
    };

    window.addEventListener('scroll', handleParallax);
    return () => window.removeEventListener('scroll', handleParallax);
  }, [tripId, packages]);

  const scrollToItinerary = () => {
    const itinerarySection = document.querySelector('.itinerary-list');
    if (itinerarySection) {
      itinerarySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToBooking = () => {
    const bookingCard = document.querySelector('.booking-card-glass');
    if (bookingCard) {
      bookingCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  if (!trip) {
    return (
      <div className="trip-details-loading">
        <Header />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading trip details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trip-details-page">
      <Header />
      
      {/* Hero Section */}
      <section className="trip-hero-modern">
        <div className="trip-hero-bg">
          <img src={getImagePath(trip.image)} alt={trip.title} className="hero-bg-img" />
          <div className="trip-hero-overlay"></div>
        </div>
        <div className="trip-hero-content">
          <div className="container">
            <Link to="/" className="back-link-modern">
              <i className="fas fa-arrow-left"></i> Back to Packages
            </Link>
            <div className="trip-hero-info">
              <span className="trip-category-badge-modern">{trip.category}</span>
              <h1 className="trip-title-display">{trip.title}</h1>
              <p className="trip-subtitle-display">{trip.description}</p>
              
              <div className="trip-meta-pills">
                <div className="meta-pill">
                  <i className="fas fa-clock"></i>
                  <span>{trip.duration}</span>
                </div>
                {trip.rating && (
                  <div className="meta-pill">
                    <i className="fas fa-star"></i>
                    <span>{trip.rating} ({trip.reviews} reviews)</span>
                  </div>
                )}
                {trip.featured && (
                  <div className="meta-pill featured">
                    <i className="fas fa-award"></i>
                    <span>Featured Trip</span>
                  </div>
                )}
              </div>

              <div className="trip-hero-actions">
                <button className="btn-hero-primary" onClick={scrollToBooking}>
                  <i className="fas fa-paper-plane"></i>
                  Book Now
                </button>
                <button className="btn-hero-secondary" onClick={scrollToItinerary}>
                  <i className="fas fa-list"></i>
                  View Itinerary
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="trip-content-section">
        <div className="container">
          <div className="trip-content-grid">
            {/* Left Column - Details */}
            <div className="trip-main-content">
              {/* Overview */}
              <div className="content-card-glass">
                <h2><i className="fas fa-info-circle"></i> Overview</h2>
                <p className="trip-description-full">{trip.description}</p>
              </div>

              {/* Highlights */}
              {trip.highlights && trip.highlights.length > 0 && (
                <div className="content-card-glass">
                  <h2><i className="fas fa-star"></i> Trip Highlights</h2>
                  <div className="highlights-grid">
                    {trip.highlights.map((highlight, index) => (
                      <div key={index} className="highlight-item-glass">
                        <i className="fas fa-check-circle"></i>
                        <span>{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Itinerary */}
              <div className="content-card-glass">
                <h2><i className="fas fa-map-marked-alt"></i> Detailed Itinerary</h2>
                <div className="itinerary-list">
                  {trip.itinerary && trip.itinerary.length > 0 ? (
                    trip.itinerary.map((day, index) => (
                      <div key={index} className="itinerary-day">
                        <div className="day-number">Day {day.day}</div>
                        <div className="day-content">
                          <h4>{day.title}</h4>
                          <p>{day.description}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="itinerary-day">
                      <div className="day-number">Day 1</div>
                      <div className="day-content">
                        <h4>Arrival and Welcome</h4>
                        <p>Arrive at your destination, meet your guide, and settle into your accommodation.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* What's Included */}
              <div className="content-card-glass">
                <h2><i className="fas fa-check-square"></i> What's Included</h2>
                {trip.included && trip.included.length > 0 ? (
                  <ul className="included-list">
                    {trip.included.map((item, index) => (
                      <li key={index}>
                        <i className="fas fa-check-circle"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="included-grid">
                    <div className="included-item">
                      <i className="fas fa-hotel"></i>
                      <span>Accommodation</span>
                    </div>
                    <div className="included-item">
                      <i className="fas fa-utensils"></i>
                      <span>Daily Breakfast</span>
                    </div>
                    <div className="included-item">
                      <i className="fas fa-bus"></i>
                      <span>Transportation</span>
                    </div>
                    <div className="included-item">
                      <i className="fas fa-user-tie"></i>
                      <span>Tour Guide</span>
                    </div>
                  </div>
                )}
              </div>

              {/* What's Not Included */}
              {trip.excluded && trip.excluded.length > 0 && (
                <div className="content-card-glass">
                  <h2><i className="fas fa-times-circle"></i> What's Not Included</h2>
                  <ul className="excluded-list">
                    {trip.excluded.map((item, index) => (
                      <li key={index}>
                        <i className="fas fa-times"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Documents - Itineraries & Brochures */}
              {trip.documents && trip.documents.length > 0 && (
                <div className="content-card-glass">
                  <h2><i className="fas fa-file-pdf"></i> Itineraries & Documents</h2>
                  <div className="documents-grid">
                    {trip.documents.map((doc, index) => (
                      <div key={index} className="document-item">
                        <div className="document-icon">
                          <i className={doc.type === 'pdf' ? 'fas fa-file-pdf' : 'fas fa-file-word'}></i>
                        </div>
                        <div className="document-details">
                          <h4>{doc.description || doc.name}</h4>
                          <p className="doc-name">{doc.name}</p>
                          <div className="document-actions">
                            {doc.type === 'pdf' ? (
                              <button 
                                onClick={() => handleDocumentDownload(doc)}
                                className="btn-doc-action download"
                              >
                                <i className="fas fa-download"></i> Download PDF
                              </button>
                            ) : (
                              <button 
                                onClick={() => handleDocumentDownload(doc)}
                                className="btn-doc-action download"
                              >
                                <i className="fas fa-download"></i> Download
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Booking Card */}
            <div className="trip-sidebar">
              <BookingForm tripTitle={trip.title} />

              <div className="contact-info-glass" style={{ marginTop: '1.5rem', padding: '1.5rem', background: 'rgba(255, 255, 255, 0.95)', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                <h4>Need Help?</h4>
                <a href={`tel:${company.phone.replace(/\s+/g, '')}`} className="contact-btn">
                  <i className="fas fa-phone"></i> Call Us
                </a>
                <a href={`mailto:${company.email}`} className="contact-btn">
                  <i className="fas fa-envelope"></i> Email Us
                </a>
              </div>
            </div>
          </div>

          {/* Safety and Cancellation Info - Full Width Section */}
          <div className="trust-badges-section">
            <div className="trust-badge">
              <div className="trust-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <div className="trust-content">
                <h4>Safety First</h4>
                <p>All our trips follow strict safety protocols and include comprehensive travel insurance.</p>
              </div>
            </div>

            <div className="trust-badge">
              <div className="trust-icon">
                <i className="fas fa-undo"></i>
              </div>
              <div className="trust-content">
                <h4>Flexible Cancellation</h4>
                <p>Free cancellation up to 7 days before departure. See full terms.</p>
              </div>
            </div>

            <div className="trust-badge">
              <div className="trust-icon">
                <i className="fas fa-headset"></i>
              </div>
              <div className="trust-content">
                <h4>24/7 Support</h4>
                <p>Our team is available round the clock to assist you during your journey.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TripDetails;
